import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, DollarSign, HelpCircle, UserPlus, BarChart2, Bell } from 'lucide-react';

const useCases = [
  {
    name: 'Customer Support',
    icon: HelpCircle,
    description: 'Resolve customer issues, provide technical support, and process service requests.',
    features: ['Issue resolution', 'Technical assistance', 'Service request processing'],
    color: '#4CAF50',
  },
  {
    name: 'Notifications',
    icon: Bell,
    description: 'Proactively deliver important updates, reminders, and alerts to customers.',
    features: ['Appointment reminders', 'Status updates', 'Service alerts'],
    color: '#2196F3',
  },
  {
    name: 'Onboarding',
    icon: UserPlus,
    description: 'Guide new customers through setup processes, educate on product features, and answer initial questions.',
    features: ['Account setup', 'Product tutorials', 'Personalized guidance'],
    color: '#9C27B0',
  },
  {
    name: 'Pre-Sales',
    icon: ShoppingCart,
    description: 'Engage potential customers, qualify leads, and schedule appointments with sales representatives.',
    features: ['Lead qualification', 'Product information', 'Appointment scheduling'],
    color: '#FF9800',
  },
  {
    name: 'Sales',
    icon: DollarSign,
    description: 'Handle transactions, process orders, and upsell related products or services to customers.',
    features: ['Order processing', 'Cross-selling and upselling', 'Payment handling'],
    color: '#E91E63',
  },
  {
    name: 'Survey',
    icon: BarChart2,
    description: 'Collect feedback, conduct market research, and gather customer opinions through interactive surveys.',
    features: ['Feedback collection', 'Satisfaction measurement', 'Market research'],
    color: '#F44336',
  },
].sort((a, b) => a.name.localeCompare(b.name));
const UseCases = ({ selectedUseCase, onUseCaseSelect }) => {
  return (
    <div>
      <h2 className="text-2xl font-bold mb-4 mt-12 mx-2 text-gray-800">Select Use Case</h2>
      <motion.div 
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-7xl mx-auto"
        style={{ minHeight: '40vh' }}
        initial="hidden"
        animate="visible"
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.1,
            },
          },
        }}
      >
        {useCases.map((useCase) => (
          <motion.div
            key={useCase.name}
            className={`flex flex-col justify-between h-full min-h-[200px] max-w-full p-4 rounded-xl cursor-pointer transition-colors duration-300 ${
              selectedUseCase === useCase.name
                ? 'bg-opacity-20 border-2'
                : 'bg-white border border-gray-200 hover:border-opacity-50'
            }`}
            style={{
              backgroundColor: selectedUseCase === useCase.name ? useCase.color + '33' : 'white',
              borderColor: selectedUseCase === useCase.name ? useCase.color : 'transparent',
              minHeight: '200px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 },
            }}
            whileHover={{ scale: 1.03, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)' }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onUseCaseSelect(useCase.name)}
          >
            <div>
              <div className="flex items-center mb-2">
                <div className={`p-2 rounded-full`} style={{ backgroundColor: useCase.color }}>
                  <useCase.icon className={`w-5 h-5 text-white`} />
                </div>
                <h3 className="text-lg font-semibold ml-3">{useCase.name}</h3>
              </div>
              <p className="text-gray-600 mb-2 line-clamp-2">{useCase.description}</p>
            </div>
            <ul className="list-disc list-inside text-xs text-gray-500 mt-1">
              {useCase.features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default UseCases;
